import React, { useEffect, useState } from 'react';
import { css } from '@emotion/css';
import PropTypes from 'prop-types';
import { get, invoke, round, some } from 'lodash';
import { useApp } from '../../context/app-context';

const toastListStyle = (top: string) => css`
  position: fixed;
  top: ${top};
  right: 3rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  z-index: 1100;

  @media (max-width: 641px) {
    top: initial;
    bottom: 1rem;
    left: 1rem;
    right: 1rem;
    flex-direction: column-reverse;
    align-items: center;
    gap: 1rem;
  }
`;

const toastStyle = css`
  display: flex;
  border-radius: 8px;
  background: #535250;
  box-shadow: 0 4px 8px 0 rgba(56, 51, 46, 0.16);
  color: #ffffff;
  padding: 1rem;
  min-width: 10rem;
  max-width: 22rem;
  min-height: 2rem;
  gap: 10px;
  user-select: none;

  & > .message {
    flex: 1;
  }

  & > svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const closeBtnStyle = css`
  cursor: pointer;
`;

const InfoIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="#8BD9B3" xmlns="http://www.w3.org/2000/svg" aria-label="info">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 23C18.0751 23 23 18.0751 23 12C23 5.92487 18.0751 1 12 1C5.92487 1 1 5.92487 1 12C1 18.0751 5.92487 23 12 23ZM19.0805 6.97495C19.3428 6.65437 19.2955 6.18185 18.9749 5.91955C18.6543 5.65726 18.1818 5.70451 17.9195 6.02509L9.66728 16.1112L5.4944 12.4533C5.18292 12.1802 4.70907 12.2114 4.43602 12.5229C4.16298 12.8344 4.19414 13.3082 4.50562 13.5812L9.262 17.7507C9.4149 17.8847 9.61568 17.9509 9.81832 17.9341C10.021 17.9173 10.2081 17.819 10.3369 17.6616L19.0805 6.97495Z"
      fill="#8BD9B3"
    />
  </svg>
);

const ErrorIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="#FBB5B5"
    className="bi bi-x-circle-fill"
    viewBox="0 0 16 16"
    aria-label="error"
  >
    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z" />
  </svg>
);

const NotificationToastsList = () => {
  const { toastsList } = useApp();
  const [top, setTop] = useState('5vh');

  useEffect(() => {
    const nav = invoke(document, 'querySelector', '.menu-nav');
    const clientRects = invoke(nav, 'getBoundingClientRect');
    setTop(`${round(get(clientRects, 'top') + get(clientRects, 'height')) + 24}px`);
  }, []);

  return (
    <div className={toastListStyle(top)}>
      {toastsList.map((toast) => {
        return (
          <NotificationToast key={toast.toastId} toastId={toast.toastId} message={toast.message} type={toast.type} />
        );
      })}
    </div>
  );
};

const NotificationToast = (props) => {
  const { toastId, message, type = 'info', duration = 3000 } = props;
  const { toastsList, closeToast } = useApp();
  const isVisible = some(toastsList, { toastId });

  const closeDialog = () => {
    closeToast(toastId);
  };

  useEffect(() => {
    if (!duration || !isVisible) {
      return;
    }

    const timeoutId = setTimeout(() => {
      closeToast(toastId);
    }, duration);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [toastId, isVisible, duration, close]);

  return (
    <div className={toastStyle} role="alert">
      {type === 'info' ? <InfoIcon /> : <ErrorIcon />}
      <div className="message" aria-label={message}>
        {message}
      </div>
      <div className={closeBtnStyle} onClick={closeDialog} role="button" aria-label="close">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M2.64652 2.64643C2.84178 2.45117 3.15837 2.45117 3.35363 2.64643L8.00005 7.29285L12.6464 2.64645C12.8417 2.45119 13.1583 2.45119 13.3536 2.64645C13.5488 2.84171 13.5488 3.15829 13.3536 3.35355L8.70715 7.99996L13.3536 12.6464C13.5489 12.8417 13.5489 13.1583 13.3536 13.3535C13.1584 13.5488 12.8418 13.5488 12.6465 13.3535L8.00005 8.70706L3.35355 13.3536C3.15829 13.5488 2.84171 13.5488 2.64645 13.3536C2.45118 13.1583 2.45118 12.8417 2.64645 12.6464L7.29294 7.99996L2.64652 3.35354C2.45126 3.15828 2.45126 2.84169 2.64652 2.64643Z"
            fill="#DDD9D5"
          />
        </svg>
      </div>
    </div>
  );
};

NotificationToast.propTypes = {
  toastId: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  type: PropTypes.string,
  duration: PropTypes.number,
};

export default NotificationToast;
export { NotificationToastsList };
